import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import { useDarkMode } from '@halo/web-lib';
import { X } from 'lucide-react';
import iconLogo from '../assets/logo/icon-logo.svg';

import dashboardIconDark from '../assets/icons/dashboardIconDark.svg';
import askHaloIconDark from '../assets/icons/askHaloIconDark.svg';
import ordersIconDarkNew from '../assets/icons/ordersIconDarkNew.svg';
import campaignsHubIconDarkNew from '../assets/icons/campaignsHubIconDarkNew.svg';
import haloMMMIconDark from '../assets/icons/haloMMMIconDark.svg';
import calendarIconDark from '../assets/custom-icons/calendarIconDark.svg';
import reportsIconDark from '../assets/custom-icons/reportsIconDark.svg';
import myIntegrationsIcon from '../assets/icons/myIntegrationsIcon.svg';
import profileIconDarkNew from '../assets/icons/profileIconDarkNew.svg';

import dashboardIconLight from '../assets/icons/dashboardIconLight.svg';
import askHaloIconLight from '../assets/icons/askHaloIconLight.svg';
import ordersIconDark from '../assets/icons/ordersIconDark.svg';
import campaignsHubIconLightNew from '../assets/icons/campaignsHubIconLightNew.png';
import haloMMMIconLight from '../assets/icons/haloMMMIconLight.svg';
import calendarIconLight from '../assets/custom-icons/calendarIconLight.svg';
import reportsIconLight from '../assets/custom-icons/reportsIconLight.svg';
import myIntegrationsIconLightNew from '../assets/icons/myIntegrationsIconLightNew2.png';
import myAccountIconLightNew from '../assets/icons/myAccountIconLightNew.png';

const mobileNavItems = [
  { to: '/', label: 'Dashboard', darkIcon: dashboardIconDark, lightIcon: dashboardIconLight },
  { to: '/ask-halo', label: 'Ask Halo', darkIcon: askHaloIconDark, lightIcon: askHaloIconLight },
  { to: '/orders', label: 'Orders', darkIcon: ordersIconDarkNew, lightIcon: ordersIconDark },
  {
    to: '/campaigns',
    label: 'Campaigns Hub',
    darkIcon: campaignsHubIconDarkNew,
    lightIcon: campaignsHubIconLightNew,
  },
  {
    to: '/halo-effect-v2',
    label: 'Halo Effect',
    darkIcon: haloMMMIconDark,
    lightIcon: haloMMMIconLight,
  },
  {
    to: '/calendar',
    label: 'Calendar',
    darkIcon: calendarIconDark,
    lightIcon: calendarIconLight,
  },
  { to: '/reports', label: 'Reports', darkIcon: reportsIconDark, lightIcon: reportsIconLight },
  {
    to: '/integrations',
    label: 'Integrations',
    darkIcon: myIntegrationsIcon,
    lightIcon: myIntegrationsIconLightNew,
  },
  {
    to: '/settings',
    label: 'Settings',
    darkIcon: profileIconDarkNew,
    lightIcon: myAccountIconLightNew,
  },
];

interface MobileNavbarProps {
  onSwitchStore: () => void;
  onLogout: () => void;
}

export function MobileNavbar({ onSwitchStore, onLogout }: MobileNavbarProps) {
  const { isDarkMode } = useDarkMode();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // Close drawer on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Close on ESC, lock body scroll while open
  useEffect(() => {
    if (!menuOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [menuOpen]);

  const drawer = createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[60] bg-black/50 transition-opacity duration-300 ${
          menuOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
      />

      {/* Panel */}
      <aside
        id="mobile-nav-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Main navigation"
        className={`fixed left-0 top-0 z-[61] h-full w-72 max-w-[85vw] overflow-y-auto shadow-xl transition-transform duration-300 ease-in-out ${
          menuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ backgroundColor: isDarkMode ? '#222222' : 'white' }}
      >
        {/* Drawer header */}
        <div
          className={`flex items-center justify-between px-4 py-3 border-b ${
            isDarkMode ? 'border-charcoalGray-500' : 'border-charcoalGray-50'
          }`}
        >
          <img src={iconLogo} alt="Halo" className="w-8 h-8" />
          <button
            type="button"
            onClick={() => setMenuOpen(false)}
            className={`rounded-md p-2 transition-colors ${
              isDarkMode
                ? 'text-white hover:bg-charcoalGray-700'
                : 'text-charcoalGray-800 hover:bg-charcoalGray-50'
            }`}
            aria-label="Close navigation"
          >
            <X className="size-5" />
          </button>
        </div>

        {/* Nav items */}
        <nav className="flex flex-col px-3 py-3">
          {mobileNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={`flex items-center gap-3 px-4 py-3 my-0.5 rounded-lg transition duration-200
                ${
                  location.pathname === item.to ||
                  (item.to !== '/' && location.pathname.startsWith(item.to))
                    ? 'bg-primary-100 dark:bg-primary-500/20 text-charcoalGray-800 dark:text-white'
                    : 'text-charcoalGray-500 dark:text-charcoalGray-200 hover:bg-primary-200 dark:hover:bg-primary-500/40'
                }
              `}
              onClick={() => setMenuOpen(false)}
            >
              <img src={isDarkMode ? item.darkIcon : item.lightIcon} alt="" className="w-5 h-5" />
              <span className="text-[14px] font-medium">{item.label}</span>
            </NavLink>
          ))}

          <div className="h-[1px] my-3 dark:bg-charcoalGray-500 bg-charcoalGray-50" />

          <button
            className="flex items-center gap-3 px-4 py-3 my-1 rounded-lg w-full
              bg-primary-100 dark:bg-primary-500/20
              text-primary-700 dark:text-primary-400 text-sm font-medium"
            onClick={() => {
              onSwitchStore();
              setMenuOpen(false);
            }}
          >
            Switch Store
          </button>

          <button
            className="flex items-center gap-3 px-4 py-3 my-1 rounded-lg w-full
              bg-warmSand-100 dark:bg-charcoalGray-900
              text-[#BD1616] text-sm font-medium"
            onClick={() => {
              onLogout();
              setMenuOpen(false);
            }}
          >
            Log Out
          </button>
        </nav>
      </aside>
    </>,
    document.body,
  );

  return (
    <div className="md:hidden">
      {/* Fixed top bar */}
      <div
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4 py-3 shadow-sm"
        style={{ backgroundColor: isDarkMode ? '#222222' : 'white' }}
      >
        <img src={iconLogo} alt="Halo" className="w-8 h-8" />
        <button
          type="button"
          className="flex flex-col gap-1.5 p-1"
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-expanded={menuOpen}
          aria-controls="mobile-nav-drawer"
          aria-label={menuOpen ? 'Close navigation' : 'Open navigation'}
        >
          <span
            className={`block w-6 h-0.5 transition-all duration-300 ${
              isDarkMode ? 'bg-white' : 'bg-charcoalGray-800'
            } ${menuOpen ? 'rotate-45 translate-y-2' : ''}`}
          />
          <span
            className={`block w-6 h-0.5 transition-all duration-300 ${
              isDarkMode ? 'bg-white' : 'bg-charcoalGray-800'
            } ${menuOpen ? 'opacity-0' : ''}`}
          />
          <span
            className={`block w-6 h-0.5 transition-all duration-300 ${
              isDarkMode ? 'bg-white' : 'bg-charcoalGray-800'
            } ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`}
          />
        </button>
      </div>

      {drawer}
    </div>
  );
}
